import React, { useState, useEffect, useMemo } from 'react';
import { AiOutlineSearch, AiOutlineFilter } from 'react-icons/ai';
import { IoClose } from 'react-icons/io5';
import FilterUI from './FilterUI';

interface Faculty {
  CODE: string;
  TITLE: string;
  TYPE: string;
  CREDITS: string;
  VENUE: string;
  SLOT: string;
  FACULTY: string;
}

interface FacultyListProps {
  data: Faculty[];
}

interface ColumnDefinition {
  key: string;
  label: string;
  width: string;
}

const CACHE_KEY = 'faculty-list-filters';
const ENTRIES_OPTIONS = [20, 30, 60, 100];

const FacultyList: React.FC<FacultyListProps> = ({ data }) => {
  // Load initial filter state from cache
  const getInitialFilterState = () => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        type: parsed.type || [],
        credits: parsed.credits || [],
        venue: parsed.venue || [],
        activeCategory: parsed.activeCategory || 'type'
      };
    }
    return {
      type: [],
      credits: [],
      venue: [],
      activeCategory: 'type'
    };
  };

  const initialState = getInitialFilterState();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState<Faculty[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState<string[]>(initialState.type);
  const [selectedCredits, setSelectedCredits] = useState<string[]>(initialState.credits);
  const [selectedVenue, setSelectedVenue] = useState<string[]>(initialState.venue);
  const [activeCategory, setActiveCategory] = useState<'type' | 'credits' | 'venue'>(initialState.activeCategory as 'type' | 'credits' | 'venue');

  // Cache filter selections
  useEffect(() => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      type: selectedType,
      credits: selectedCredits,
      venue: selectedVenue,
      activeCategory
    }));
  }, [selectedType, selectedCredits, selectedVenue, activeCategory]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const types = new Set<string>();
    const credits = new Set<string>();
    const venues = new Set<string>();

    data.forEach(item => {
      if (item.TYPE?.trim()) types.add(item.TYPE.trim());
      if (item.CREDITS?.trim()) credits.add(item.CREDITS.trim());
      if (item.VENUE?.trim() && item.VENUE.trim().toLowerCase() !== 'nil') {
        const venuePrefix = item.VENUE.trim().match(/[a-zA-Z]+/);
        if (venuePrefix) {
          venues.add(venuePrefix[0].toUpperCase());
        }
      }
    });

    return {
      types: Array.from(types).sort(),
      credits: Array.from(credits).sort((a, b) => parseFloat(a) - parseFloat(b)),
      venues: Array.from(venues).sort()
    };
  }, [data]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedType, selectedCredits, selectedVenue]);

  // Prepare searchable data once
  const searchableData = useMemo(() => {
    return data.map(item => ({
      original: item,
      searchText: (
        (item.CODE || '') +
        ' ' +
        (item.TITLE || '') +
        ' ' +
        (item.SLOT || '') +
        ' ' +
        (item.FACULTY || '')
      ).toLowerCase()
    }));
  }, [data]);

  // Fast search and filter implementation
  useEffect(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    
    let results = [...data];

    // Apply search filter
    if (trimmedQuery) {
      results = searchableData
        .filter(item => item.searchText.includes(trimmedQuery))
        .map(item => item.original);
    }

    // Apply type filter
    if (selectedType.length > 0) {
      results = results.filter(item => item.TYPE?.trim() && selectedType.includes(item.TYPE.trim()));
    }

    // Apply credits filter
    if (selectedCredits.length > 0) {
      results = results.filter(item => item.CREDITS?.trim() && selectedCredits.includes(item.CREDITS.trim()));
    }

    // Apply venue filter
    if (selectedVenue.length > 0) {
      results = results.filter(item => {
        if (item.VENUE?.trim()) {
          const venuePrefix = item.VENUE.trim().match(/[a-zA-Z]+/);
          if (venuePrefix) {
            return selectedVenue.includes(venuePrefix[0].toUpperCase());
          }
        }
        return false;
      });
    }

    setFilteredData(results);
  }, [searchQuery, selectedType, selectedCredits, selectedVenue, searchableData, data]);

  const handleTypeSelect = (type: string) => {
    setSelectedType(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleCreditsSelect = (credits: string) => {
    setSelectedCredits(prev => 
      prev.includes(credits) ? prev.filter(c => c !== credits) : [...prev, credits]
    );
  };

  const handleVenueSelect = (venue: string) => {
    setSelectedVenue(prev => 
      prev.includes(venue) ? prev.filter(v => v !== venue) : [...prev, venue]
    );
  };

  // Columns configuration
  const columns: ColumnDefinition[] = [
    { key: 'CODE', label: 'Course Code', width: '15%' },
    { key: 'TYPE', label: 'Type', width: '10%' },
    { key: 'TITLE', label: 'Course Title', width: '25%' },
    { key: 'CREDITS', label: 'Credits', width: '10%' },
    { key: 'SLOT', label: 'Slot', width: '10%' },
    { key: 'FACULTY', label: 'Faculty', width: '20%' },
    { key: 'VENUE', label: 'Venue', width: '10%' }
  ];

  // Get visible columns for the current results
  const visibleColumns: ColumnDefinition[] = useMemo(() => {
    if (filteredData.length === 0) return columns;
    
    return columns.filter(col => 
      filteredData.some(item => item[col.key as keyof Faculty]?.trim())
    );
  }, [filteredData]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + entriesPerPage);

  return (
    <div className="px-4 min-h-screen bg-white">
      {/* Search Section */}
      <div className="py-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-8">Faculty List Search</h1>
          
          {/* Search Bar and Filter Button */}
          <div className="w-full px-4 sm:px-6 lg:px-8 mb-8">
            <div className="relative max-w-3xl mx-auto">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by course code, title, faculty name..."
                    className="w-full px-5 py-4 h-[3.75rem] rounded-xl border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent shadow-sm text-gray-900 placeholder-gray-500 text-base pl-12"
                  />
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <AiOutlineSearch className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-5 h-[3.75rem] rounded-xl transition-all duration-200 flex items-center gap-2.5 shadow-sm
                    ${showFilters 
                      ? 'bg-black text-white hover:bg-gray-900' 
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
                >
                  <div className="relative">
                    <AiOutlineFilter className="h-5 w-5" />
                    {(selectedType.length > 0 || selectedCredits.length > 0 || selectedVenue.length > 0) && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-black rounded-full flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">
                          {selectedType.length + selectedCredits.length + selectedVenue.length}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="hidden sm:inline font-medium">Filters</span>
                </button>
              </div>

              {/* Filter UI Component */}
              <FilterUI
                showFilters={showFilters}
                selectedType={selectedType}
                selectedCredits={selectedCredits}
                selectedVenue={selectedVenue}
                activeCategory={activeCategory}
                filterOptions={filterOptions}
                onTypeSelect={handleTypeSelect}
                onCreditsSelect={handleCreditsSelect}
                onVenueSelect={handleVenueSelect}
                onClearType={() => setSelectedType([])}
                onClearCredits={() => setSelectedCredits([])}
                onClearVenue={() => setSelectedVenue([])}
                onCategorySelect={setActiveCategory}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {filteredData.length > 0 && (
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 text-sm gap-4">
            <div className="flex items-center space-x-2 text-gray-900">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-black focus:border-transparent hover:border-gray-400 transition-colors"
              >
                {ENTRIES_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span>entries</span>
            </div>
            <div className="text-right">
              <span className="bg-black text-white px-3 py-1.5 rounded-lg font-medium">
                Found {filteredData.length} results
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="w-full overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="min-w-full">
                  {/* Column Headers */}
              <div className="flex items-center justify-between w-full px-6 py-4 border-b border-gray-200">
                {visibleColumns.map(col => (
                  <div key={col.key} style={{ width: col.width }} className="shrink-0 font-semibold text-gray-900 text-center">
                    {col.label}
                  </div>
                ))}
                  </div>

                  {/* Faculty List */}
              <div className="divide-y divide-gray-200">
                    {paginatedData.map((item, index) => (
                      <div 
                        key={index} 
                    className="flex items-center justify-between w-full px-6 py-5 hover:bg-white transition-colors group"
                  >
                    {visibleColumns.map(col => (
                      <div key={col.key} style={{ width: col.width }} className="shrink-0 text-center">
                        {col.key === 'CODE' && item.CODE?.trim() && (
                          <span className="font-semibold text-gray-900">{item.CODE}</span>
                        )}
                        {col.key === 'TYPE' && item.TYPE?.trim() && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              {item.TYPE}
                            </span>
                        )}
                        {col.key === 'TITLE' && item.TITLE?.trim() && (
                          <div className="flex flex-col">
                            <span className="text-gray-900 font-bold truncate group-hover:text-blue-700 transition-colors" title={item.TITLE}>{item.TITLE}</span>
                          </div>
                        )}
                        {col.key === 'CREDITS' && item.CREDITS?.trim() && (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-gray-900 font-semibold">
                            {item.CREDITS}
                          </span>
                        )}
                        {col.key === 'SLOT' && item.SLOT?.trim() && (
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {item.SLOT}
                          </span>
                        )}
                        {col.key === 'FACULTY' && item.FACULTY?.trim() && (
                          <span className="text-gray-700 truncate block font-medium group-hover:text-gray-900 transition-colors" title={item.FACULTY}>{item.FACULTY}</span>
                        )}
                        {col.key === 'VENUE' && item.VENUE?.trim() && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-gray-900">
                            {item.VENUE}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-8 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-700 gap-4">
            <div className="font-medium">
              Showing <span className="text-gray-900">{startIndex + 1}</span> to <span className="text-gray-900">{Math.min(startIndex + entriesPerPage, filteredData.length)}</span> of <span className="text-gray-900">{filteredData.length}</span> entries
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white text-gray-700 transition-colors font-medium"
              >
                Previous
              </button>
              <span className="px-4 py-2 bg-gray-100 rounded-lg font-medium text-gray-900">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white text-gray-700 transition-colors font-medium"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyList; 